/**
 * Manages ChatKit thread memory in sessionStorage.
 * Used to automatically reopen the most recent thread if it's recent enough.
 * Uses sessionStorage (per-tab) instead of localStorage to allow multiple
 * independent chat sessions in different tabs.
 */

const STORAGE_KEY = 'chatkit.lastThread';
const DEFAULT_MAX_AGE_HOURS = 24; // Default: consider thread "recent" if within 24 hours

/**
 * Gets the last thread ID and timestamp from storage
 * @returns {{ threadId: string, timestamp: number } | null}
 */
export function getLastThread() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);
    return {
      threadId: data.threadId,
      timestamp: data.timestamp
    };
  } catch {
    return null;
  }
}

/**
 * Saves the current thread ID to storage
 * @param {string | null | undefined} threadId
 */
export function saveLastThread(threadId) {
  try {
    if (threadId == null || threadId === '') {
      return;
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      threadId,
      timestamp: Date.now()
    }));
  } catch {
    // sessionStorage might not be available (e.g., in private browsing)
    console.warn('[ChatKit] Could not save last thread to sessionStorage');
  }
}

/**
 * Clears the stored last thread.
 */
export function clearLastThread() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Checks if a thread is recent enough to be automatically reopened
 * @param {number} timestamp - The timestamp when the thread was last active
 * @param {number} maxAgeHours - Maximum age in hours (default: 24)
 * @returns {boolean}
 */
export function isThreadRecent(timestamp, maxAgeHours = DEFAULT_MAX_AGE_HOURS) {
  if (!timestamp) return false;

  const maxAgeMs = maxAgeHours * 60 * 60 * 1000;
  const age = Date.now() - timestamp;
  return age < maxAgeMs;
}

/**
 * Gets the initial thread ID to use when mounting ChatKit.
 * Returns the last thread ID if it's recent enough, otherwise null.
 * @param {number} maxAgeHours - Maximum age in hours (default: 24)
 * @returns {string | null}
 */
export function getInitialThreadId(maxAgeHours = DEFAULT_MAX_AGE_HOURS) {
  const lastThread = getLastThread();
  if (!lastThread) return null;

  if (isThreadRecent(lastThread.timestamp, maxAgeHours)) {
    return lastThread.threadId;
  }

  // Thread is too old, clear it
  clearLastThread();
  return null;
}
